"use client";

import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { addToast } from "@heroui/react";
import { useAuth } from "@webuild-ai/react-auth";
import { Brand, DashboardConfig } from "@webuild-ai/react-auth";

import { useTheme } from "@/hooks/use-theme";
import { getTokenExpiryWithFallback } from "@/utils/tokenUtils";
import { logger } from "@/telemetry";

import { DashboardLayout } from "./DashboardLayout";
import { DashboardLoading } from "./DashboardLoading";
import { DashboardError } from "./DashboardError";

interface IframeDashboardProps {
  /** Dashboard configuration */
  config: DashboardConfig;
  /** Current brand configuration */
  brand: Brand;
  /** Optional URL override (e.g., from query param) */
  urlOverride?: string;
  /** Optional initial tab to display (from URL query param) */
  initialTab?: string;
  /** Optional initial sub-path for detail page deep-linking (e.g., "/partners/1") */
  initialPath?: string;
}

const DEFAULT_SANDBOX = "allow-scripts allow-same-origin allow-popups allow-forms";

/**
 * Iframe-based dashboard embedding component.
 * Used for external vendors (Looker, PowerBI) or different tech stack dashboards.
 *
 * Features:
 * - Loading state with spinner overlay
 * - Error state with retry functionality
 * - Configurable sandbox permissions
 * - URL override support for demos
 * - Tab-based URL routing for bookmarkable dashboard tabs
 */
export const IframeDashboard: React.FC<IframeDashboardProps> = ({
  config,
  brand,
  urlOverride,
  initialTab,
  initialPath,
}) => {
  const { t, i18n } = useTranslation();
  const { theme, setLightTheme, setDarkTheme } = useTheme();
  const { user, logout, getAccessTokenSilently } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  // Track the last path received from iframe to prevent echo loops
  const lastReceivedPath = useRef<string | null>(null);

  // Dashboard URL segment for constructing Pathway URLs (e.g., "ncrm")
  const urlSegment = brand.dashboard?.urlSegment ?? "dashboard";

  // Priority: urlOverride > config.url > env variable
  const dashboardUrl = urlOverride || config.url || import.meta.env.VITE_DASHBOARD_URL;
  const sandboxPermissions = config.sandbox || DEFAULT_SANDBOX;

  // Get current language with fallback for test environment
  const currentLanguage = i18n?.language || "en-GB";

  // Get current theme with fallback
  const currentTheme = theme || "dark";

  // Get cloud provider from environment variable (defaults to 'azure')
  const cloudProvider = import.meta.env.VITE_CLOUD_PROVIDER || "azure";

  // Compute user initials from name
  const userInitials = useMemo(() => {
    if (!user?.name) return "";
    const parts = user.name.trim().split(/\s+/);

    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();

    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }, [user?.name]);

  // Listen for navigation messages from iframe
  useEffect(() => {
    // Derive the trusted origin from the configured dashboard URL
    let trustedOrigin: string | null = null;

    if (dashboardUrl) {
      try {
        const url = new URL(dashboardUrl, window.location.origin);

        trustedOrigin = url.origin;
      } catch {
        trustedOrigin = null;
      }
    }

    const handleMessage = (event: MessageEvent) => {
      // Require a known trusted origin
      if (!trustedOrigin || event.origin !== trustedOrigin) {
        return;
      }

      // If we have both an iframe reference and event source, ensure the message comes from that iframe
      // Note: event.source may be null in test environments
      if (
        iframeRef.current?.contentWindow &&
        event.source &&
        event.source !== iframeRef.current.contentWindow
      ) {
        return;
      }

      if (event.data?.type === "NAVIGATE" && event.data?.path) {
        const path = event.data.path as string;
        // Paths that are Pathway-level routes (e.g., /settings) navigate directly.
        // Paths that belong to the embedded app get prefixed with the dashboard segment.
        const pathwayRoutes = [
          "/settings",
          "/chat",
          "/workflows",
          "/datahub",
          "/prompts",
          "/models",
        ];
        const isPathwayRoute = pathwayRoutes.some((r) => path === r || path.startsWith(r + "/"));

        navigate(isPathwayRoute ? path : `/${urlSegment}${path}`);
      }

      // Handle tab change notifications from iframe - update URL for bookmarking
      // Supports both tab-only (list pages) and path-based (detail pages) URLs
      // Empty tab string means navigation to home - clear the URL param
      if (event.data?.type === "TAB_CHANGED" && typeof event.data?.tab === "string") {
        const tab = event.data.tab;
        const path: string | undefined = event.data.path;

        if (path && path !== "/") {
          // Detail page: use URL segments (e.g., /ncrm/partners/1)
          lastReceivedPath.current = `/${urlSegment}${path}`;
          navigate(`/${urlSegment}${path}`, { replace: true });
        } else if (tab) {
          // List page: use query param (e.g., /ncrm?tab=partners)
          lastReceivedPath.current = null;
          navigate(`/${urlSegment}?tab=${tab}`, { replace: true });
        } else {
          // Home: clear to base URL
          lastReceivedPath.current = null;
          navigate(`/${urlSegment}`, { replace: true });
        }
      }

      // Handle theme change from iframe - sync Pathway's theme
      if (event.data?.type === "THEME_CHANGE" && typeof event.data?.theme === "string") {
        const newTheme = event.data.theme;

        if (newTheme === "light") {
          setLightTheme();
        } else if (newTheme === "dark") {
          setDarkTheme();
        }
      }

      // Handle logout request from iframe
      if (event.data?.type === "REQUEST_LOGOUT") {
        void logout();
      }

      // Handle settings navigation request from iframe
      if (event.data?.type === "NAVIGATE_TO_SETTINGS") {
        navigate("/settings");
      }

      // Handle error report from iframe
      if (event.data?.type === "ERROR_REPORT" && event.data?.error) {
        const { message, source, timestamp } = event.data.error;

        logger.error("[IframeDashboard] Error from embedded app", undefined, {
          message,
          source,
          timestamp: new Date(timestamp).toISOString(),
          stack: event.data.error.stack,
          componentStack: event.data.error.componentStack,
        });

        addToast({
          title: t("Dashboard Error"),
          description: message || t("An error occurred in the embedded dashboard"),
          color: "danger",
        });
      }

      // Handle auth request from iframe - respond immediately with auth token
      // This fixes the chicken-and-egg problem where iframe waits for auth before
      // firing onLoad, but Pathway only sends user info on onLoad
      if (event.data?.type === "AUTH_REQUEST" && user) {
        if (iframeRef.current?.contentWindow) {
          // Get access token and send AUTH_TOKEN message
          void (async () => {
            try {
              const accessToken = await getAccessTokenSilently?.();

              if (accessToken && iframeRef.current?.contentWindow) {
                iframeRef.current.contentWindow.postMessage(
                  {
                    type: "AUTH_TOKEN",
                    accessToken,
                    expiresAt: getTokenExpiryWithFallback(accessToken),
                    user: {
                      sub: user.id,
                      name: user.name,
                      email: user.email,
                    },
                  },
                  event.origin,
                );
              }
            } catch (error) {
              logger.error("[IframeDashboard] Failed to get access token", error);
              // Fallback to USER_INFO if token fetch fails
              iframeRef.current?.contentWindow?.postMessage(
                {
                  type: "USER_INFO",
                  user: {
                    name: user.name,
                    email: user.email,
                    initials: userInitials,
                  },
                },
                event.origin,
              );
            }
          })();
          // Also send current theme and language
          iframeRef.current.contentWindow.postMessage(
            { type: "THEME_CHANGE", theme: currentTheme },
            event.origin,
          );
          iframeRef.current.contentWindow.postMessage(
            { type: "LANGUAGE_CHANGE", language: currentLanguage },
            event.origin,
          );
          // Send current navigation state for bookmarkable URLs
          // Priority: URL sub-path (detail pages) > ?tab= query param (list pages)
          const prefix = `/${urlSegment}`;

          if (location.pathname.startsWith(prefix) && location.pathname.length > prefix.length) {
            const subPath = location.pathname.slice(prefix.length);
            const firstSegment = subPath.split("/").filter(Boolean)[0] || "";

            iframeRef.current.contentWindow.postMessage(
              { type: "SET_TAB", tab: firstSegment, path: subPath },
              event.origin,
            );
          } else {
            const currentTab = searchParams.get("tab");

            if (currentTab) {
              iframeRef.current.contentWindow.postMessage(
                { type: "SET_TAB", tab: currentTab },
                event.origin,
              );
            }
          }
        }
      }

      // Handle tab request from iframe - respond with current navigation state from URL
      // Priority: URL sub-path (detail pages) > ?tab= query param (list pages)
      if (event.data?.type === "REQUEST_TAB") {
        const prefix = `/${urlSegment}`;

        if (
          location.pathname.startsWith(prefix) &&
          location.pathname.length > prefix.length &&
          iframeRef.current?.contentWindow
        ) {
          const subPath = location.pathname.slice(prefix.length);
          const firstSegment = subPath.split("/").filter(Boolean)[0] || "";

          iframeRef.current.contentWindow.postMessage(
            { type: "SET_TAB", tab: firstSegment, path: subPath },
            event.origin,
          );
        } else {
          const currentTab = searchParams.get("tab");

          if (currentTab && iframeRef.current?.contentWindow) {
            iframeRef.current.contentWindow.postMessage(
              { type: "SET_TAB", tab: currentTab },
              event.origin,
            );
          }
        }
      }
    };

    window.addEventListener("message", handleMessage);

    return () => window.removeEventListener("message", handleMessage);
  }, [
    navigate,
    location.pathname,
    dashboardUrl,
    setLightTheme,
    setDarkTheme,
    logout,
    user,
    userInitials,
    currentTheme,
    currentLanguage,
    t,
    getAccessTokenSilently,
    searchParams,
    urlSegment,
  ]);

  // Send language to iframe when it changes
  useEffect(() => {
    if (iframeRef.current?.contentWindow && !isLoading) {
      iframeRef.current.contentWindow.postMessage(
        { type: "LANGUAGE_CHANGE", language: currentLanguage },
        "*",
      );
    }
  }, [currentLanguage, isLoading]);

  // Send theme to iframe when it changes
  useEffect(() => {
    if (iframeRef.current?.contentWindow && !isLoading) {
      iframeRef.current.contentWindow.postMessage(
        { type: "THEME_CHANGE", theme: currentTheme },
        "*",
      );
    }
  }, [currentTheme, isLoading]);

  // Send cloud provider to iframe when it loads
  useEffect(() => {
    if (iframeRef.current?.contentWindow && !isLoading) {
      iframeRef.current.contentWindow.postMessage(
        { type: "CLOUD_PROVIDER_CHANGE", provider: cloudProvider },
        "*",
      );
    }
  }, [cloudProvider, isLoading]);

  // Send auth token to iframe when user changes
  useEffect(() => {
    if (iframeRef.current?.contentWindow && !isLoading && user) {
      void (async () => {
        try {
          const accessToken = await getAccessTokenSilently?.();

          if (accessToken && iframeRef.current?.contentWindow) {
            iframeRef.current.contentWindow.postMessage(
              {
                type: "AUTH_TOKEN",
                accessToken,
                expiresAt: getTokenExpiryWithFallback(accessToken),
                user: {
                  sub: user.id,
                  name: user.name,
                  email: user.email,
                },
              },
              "*",
            );
          }
        } catch {
          // Fallback to USER_INFO if token fetch fails
          iframeRef.current?.contentWindow?.postMessage(
            {
              type: "USER_INFO",
              user: {
                name: user.name,
                email: user.email,
                initials: userInitials,
              },
            },
            "*",
          );
        }
      })();
    }
  }, [user, userInitials, isLoading, getAccessTokenSilently]);

  // Sync navigation from URL changes (browser back/forward)
  // Handles both ?tab= query params and URL segment sub-paths
  useEffect(() => {
    if (!iframeRef.current?.contentWindow || isLoading) return;

    const prefix = `/${urlSegment}`;

    // Check for URL sub-path first (detail pages like /ncrm/partners/1)
    if (location.pathname.startsWith(prefix) && location.pathname.length > prefix.length) {
      // Skip if this was just set by a TAB_CHANGED from the iframe (prevent echo)
      if (lastReceivedPath.current === location.pathname) {
        lastReceivedPath.current = null;

        return;
      }
      const subPath = location.pathname.slice(prefix.length);
      const firstSegment = subPath.split("/").filter(Boolean)[0] || "";

      iframeRef.current.contentWindow.postMessage(
        { type: "SET_TAB", tab: firstSegment, path: subPath },
        "*",
      );
    } else {
      // Fallback to ?tab= query param (list pages)
      const tabFromUrl = searchParams.get("tab");

      if (tabFromUrl) {
        iframeRef.current.contentWindow.postMessage({ type: "SET_TAB", tab: tabFromUrl }, "*");
      }
    }
  }, [location.pathname, searchParams, isLoading, urlSegment]);

  const handleIframeLoad = useCallback(() => {
    setIsLoading(false);
    setHasError(false);
    // Send initial language, theme, cloud provider, auth token, and tab to iframe
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(
        { type: "LANGUAGE_CHANGE", language: currentLanguage },
        "*",
      );
      iframeRef.current.contentWindow.postMessage(
        { type: "THEME_CHANGE", theme: currentTheme },
        "*",
      );
      iframeRef.current.contentWindow.postMessage(
        { type: "CLOUD_PROVIDER_CHANGE", provider: cloudProvider },
        "*",
      );
      if (user) {
        // Send AUTH_TOKEN with real JWT
        void (async () => {
          try {
            const accessToken = await getAccessTokenSilently?.();

            if (accessToken && iframeRef.current?.contentWindow) {
              iframeRef.current.contentWindow.postMessage(
                {
                  type: "AUTH_TOKEN",
                  accessToken,
                  expiresAt: getTokenExpiryWithFallback(accessToken),
                  user: {
                    sub: user.id,
                    name: user.name,
                    email: user.email,
                  },
                },
                "*",
              );
            }
          } catch {
            // Fallback to USER_INFO if token fetch fails
            iframeRef.current?.contentWindow?.postMessage(
              {
                type: "USER_INFO",
                user: {
                  name: user.name,
                  email: user.email,
                  initials: userInitials,
                },
              },
              "*",
            );
          }
        })();
      }
      // Send initial navigation state to iframe
      // Priority: sub-path (detail pages) > tab query param (list pages)
      if (initialPath) {
        const firstSegment = initialPath.split("/").filter(Boolean)[0] || "";

        iframeRef.current.contentWindow.postMessage(
          { type: "SET_TAB", tab: firstSegment, path: initialPath },
          "*",
        );
      } else if (initialTab) {
        iframeRef.current.contentWindow.postMessage({ type: "SET_TAB", tab: initialTab }, "*");
      }
    }
  }, [
    currentLanguage,
    currentTheme,
    cloudProvider,
    user,
    userInitials,
    initialTab,
    initialPath,
    getAccessTokenSilently,
  ]);

  const handleIframeError = useCallback(() => {
    setIsLoading(false);
    setHasError(true);
  }, []);

  const handleRetry = useCallback(() => {
    setIsLoading(true);
    setHasError(false);
    setRetryKey((prev) => prev + 1);
  }, []);

  if (!dashboardUrl) {
    return (
      <DashboardLayout brand={brand} subtitle={config.subtitle} title={config.title}>
        <DashboardError message="No dashboard URL configured" />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout brand={brand} subtitle={config.subtitle} title={config.title}>
      {isLoading && <DashboardLoading />}
      {hasError ? (
        <DashboardError onRetry={handleRetry} />
      ) : (
        <iframe
          key={retryKey}
          ref={iframeRef}
          className={`w-full h-full border-0 transition-opacity duration-300 ${
            isLoading ? "opacity-0" : "opacity-100"
          }`}
          sandbox={sandboxPermissions}
          src={dashboardUrl}
          title={config.title || "Dashboard"}
          onError={handleIframeError}
          onLoad={handleIframeLoad}
        />
      )}
    </DashboardLayout>
  );
};
